import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, Store, Zap, ShoppingCart, MessageCircle, Info } from 'lucide-react';
import { marketplaces, brand } from '../mock';
import BrandLogo from './BrandLogo';
import ItemDetailDialog from './ItemDetailDialog';
import CheckoutDialog from './CheckoutDialog';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Marketplaces() {
  const [openKey, setOpenKey] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    axios.get(`${API}/plans`).then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  const getPlan = (planId) => plans.find((p) => p.id === planId);

  const handleBuyNow = (m) => {
    const plan = getPlan(m.planId);
    if (!plan) {
      window.open(
        `https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(
          `Hi EC Safetrade, I want to sell on ${m.name}. Please share pricing and details.`
        )}`,
        '_blank'
      );
      return;
    }
    setCheckoutPlan(plan);
  };

  const handleAddToCart = (m) => {
    const plan = getPlan(m.planId);
    if (!plan) {
      window.open(
        `https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(
          `Hi EC Safetrade, I want to sell on ${m.name}. Please share pricing and details.`
        )}`,
        '_blank'
      );
      return;
    }
    add(plan);
  };

  const stop = (e) => e.stopPropagation();

  return (
    <section id="marketplaces" className="section bg-gradient-to-b from-white to-neutral-50">
      <div className="container-x">
        <div className="text-center mb-14">
          <div className="eyebrow">Marketplaces we cover</div>
          <h2 className="section-title mt-3">
            Sell Everywhere. <span className="grad-text">Grow Everywhere.</span>
          </h2>
          <p className="section-sub mt-4 mx-auto">
            Choose your preferred marketplace and explore our expert account management solutions
            designed to scale your eCommerce business.
          </p>
          <p className="mt-3 text-xs font-semibold text-[color:var(--brand-green-dark)]">
            Tap the card for full details, or use Buy Now / Add to Cart below
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {marketplaces.map((m) => (
            <div
              key={m.slug}
              onClick={() => setOpenKey(m.name)}
              className="card-hover group relative rounded-3xl overflow-hidden shadow-lg cursor-pointer bg-white text-left flex flex-col"
              style={{ border: `2px solid ${m.color}30` }}
            >
              {/* Top logo panel */}
              <div
                className="relative h-36 flex items-center justify-center p-6"
                style={{
                  background: `linear-gradient(135deg, ${m.color}12 0%, #ffffff 100%)`,
                  borderBottom: `1px solid ${m.color}20`,
                }}
              >
                <div
                  className="absolute top-3 right-3 w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg"
                  style={{ background: m.color }}
                >
                  <Store size={18} />
                </div>
                <BrandLogo
                  slug={m.slug}
                  className="h-14 w-auto max-w-[80%] transition-transform duration-500 group-hover:scale-110"
                />
              </div>

              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-bold text-[color:var(--ink)] mb-2 text-base">
                  {m.name} Seller Services
                </h3>
                <p className="text-[13px] text-[color:var(--ink-soft)] leading-relaxed mb-4 flex-1">
                  {m.desc}
                </p>

                {/* Action buttons */}
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={(e) => { stop(e); handleBuyNow(m); }}
                    className="w-full inline-flex items-center justify-center gap-2 font-semibold py-2.5 rounded-full text-sm text-white shadow-md hover:opacity-90 transition"
                    style={{ background: m.color }}
                  >
                    {m.planId ? (<><Zap size={15} /> Buy Now</>) : (<><MessageCircle size={15} /> Get Quote</>)}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { stop(e); handleAddToCart(m); }}
                    className="w-full inline-flex items-center justify-center gap-2 font-semibold py-2.5 rounded-full text-sm border-2 hover:bg-neutral-50 transition"
                    style={{ borderColor: `${m.color}66`, color: m.color }}
                  >
                    {m.planId ? (<><ShoppingCart size={15} /> Add to Cart</>) : (<><MessageCircle size={15} /> Ask on WhatsApp</>)}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { stop(e); setOpenKey(m.name); }}
                    className="w-full inline-flex items-center justify-center gap-1 text-xs font-semibold pt-1 text-[color:var(--ink-soft)] hover:text-[color:var(--brand-green)] transition"
                  >
                    <Info size={13} /> Learn more <ArrowRight size={12} />
                  </button>
                </div>
              </div>

              {/* accent stripe */}
              <div className="absolute left-0 top-0 h-full w-1" style={{ background: m.color }} />
            </div>
          ))}
        </div>

        <div
          className="mt-14 relative rounded-3xl p-8 lg:p-10 overflow-hidden shadow-xl"
          style={{ background: 'linear-gradient(135deg, #1F8B4C 0%, #146638 100%)' }}
        >
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[color:var(--brand-orange)]/25 blur-3xl" />
          <div className="absolute -left-10 -bottom-16 w-56 h-56 rounded-full bg-[color:var(--brand-yellow)]/25 blur-3xl" />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-white text-2xl md:text-3xl font-black">
                Does Your Online Business Need A Boost?
              </h3>
              <p className="text-white/85 mt-2 text-sm max-w-2xl">
                We don’t just manage your store — we enhance it to attract high-quality leads.
                Let our marketplace experts put your product at the top of E-Commerce searches.
              </p>
            </div>
            <a href="/pricing" className="btn-orange whitespace-nowrap">
              See All Plans <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>

      <ItemDetailDialog
        itemKey={openKey}
        open={!!openKey}
        onOpenChange={(v) => !v && setOpenKey(null)}
      />
      <CheckoutDialog
        plan={checkoutPlan}
        open={!!checkoutPlan}
        onOpenChange={(v) => !v && setCheckoutPlan(null)}
      />
    </section>
  );
}
